import { t } from "i18next";

import ReactEcharts from "echarts-for-react";
import { DateTime, Duration, Interval } from "luxon";
import { useRef, useState } from "react";
import { hexColor } from "../../../../../enums/colors";
import { stringToColour } from "../../../../../lib/utils";
import { useAppStore } from "../../../../../stores/RootStore";
import { DateShift, Equipment, Event, ParameterViolationWithStart } from "../../../../../types/types";

import _, { isNaN, isUndefined } from "lodash";
import { TbChevronDown, TbChevronUp, TbPointFilled } from "react-icons/tb";
import { useDimensions } from "../../../../../hooks/useDimensions";
import { DataRow } from "../../../../../stores/TabsStore";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../../ui/Popover";
import DeviceSensorData from "../../../../devices/deviceItemTabs/deviceNetworkTab/DeviceSensorData";
// import TimelineChart from "./TimelineChart";
// import { HourHeatMap } from "./HourHeatMap";

import { calculateInteractions } from "../../../../../lib/calculations";
import { Separator } from "../../../../../ui/Separator";
import { HourHeatMap } from "./HourHeatMap";
import TimelineChart from "./TimelineChart";

export function EquipmentDay({equipment, data}:{equipment: Equipment, data: DataRow[]}) {
  const dataByDays = Object.entries(_.groupBy(data, el => el.dateShift.start_day.toSQLDate()))
  return (
    <div className="flex flex-col space-y-2 my-6">
      <div className="grid grid-cols-12 text-sm px-4">
        <div className="col-span-4 flex">
          <div className="font-semibold flex">{t('date')}</div>
        </div>
        
        <div className="flex justify-end col-span-2">{t('worked')}</div>
        <div className="flex justify-end col-span-2">{t('stopped')}</div>
        <div className="flex justify-end col-span-2">{t('turned_off')}</div>
        <div className="flex justify-end col-span-2">{t('total')}</div>
      </div>
      {
        dataByDays.length > 0 ? _.orderBy(dataByDays, el => el[0], "desc").map(([day, dayData], i) => (
          <div key={i}><DayData day={day} equipment={equipment} data={dayData}/></div>
        ))
        :
        <div>{t('no_data')}</div>
      }
    </div>
  )
}

function DayData({day, equipment, data}:{day: string; equipment: Equipment, data: DataRow[]}) {
  return (
    <div className="border flex flex-col rounded text-sm">
      <div className="w-full flex flex-col">
        <div className="grid grid-cols-12 py-2 px-4">
          <div className="col-span-2 flex flex-col">
            <div className="flex items-center">
              <div className="font-semibold px-4">{DateTime.fromSQL(day).toFormat("dd.MM.yyyy")} ({DateTime.fromSQL(day).weekdayLong})</div>
            </div>
            {/* <div className="pl-8">{data.startWork.isValid ? <>{data.startWork.toFormat("HH:mm:ss")} - {data.endWork.toFormat("HH:mm:ss")}</> : "-"}</div> */}
          </div>
          <div className="col-span-2 flex flex-col items-end">
            <span className="justify-end">{t('availability')}</span>
            <span className="justify-end">{t('unavailability')}</span>
          </div>

          <div className="col-span-2 flex flex-col items-end">
            <div className="flex space-x-2 font-semibold">
              <span>{Duration.fromObject({seconds: _.sumBy(data, "availability.worked.seconds")}).toFormat("hh:mm:ss")}</span>
              <span>{isNaN((_.sumBy(data, "availability.worked.seconds") / _.sumBy(data, "availability.total.seconds"))) ? <></> : <>{(_.sumBy(data, "availability.worked.seconds") / _.sumBy(data, "availability.total.seconds") * 100).toFixed(1)}%</>}</span>
            </div>
            <div className="flex space-x-2">
              <span>{Duration.fromObject({seconds: _.sumBy(data, "unavailability.worked.seconds")}).toFormat("hh:mm:ss")}</span>
              <span>{isNaN((_.sumBy(data, "unavailability.worked.seconds") / _.sumBy(data, "unavailability.total.seconds"))) ? <></> : <>{(_.sumBy(data, "unavailability.worked.seconds") / _.sumBy(data, "unavailability.total.seconds") * 100).toFixed(1)}%</>}</span>
            </div>
          </div>
          <div className="col-span-2 flex flex-col items-end">
            <div className="flex space-x-2 font-semibold">
              <span>{Duration.fromObject({seconds: _.sumBy(data, "availability.stopped.seconds")}).toFormat("hh:mm:ss")}</span>
              <span>{isNaN((_.sumBy(data, "availability.stopped.seconds") / _.sumBy(data, "availability.total.seconds"))) ? <></> : <>{(_.sumBy(data, "availability.stopped.seconds") / _.sumBy(data, "availability.total.seconds") * 100).toFixed(1)}%</>}</span>
            </div>
            <div className="flex space-x-2">
              <span>{Duration.fromObject({seconds: _.sumBy(data, "unavailability.stopped.seconds")}).toFormat("hh:mm:ss")}</span>
              <span>{isNaN((_.sumBy(data, "unavailability.stopped.seconds") / _.sumBy(data, "unavailability.total.seconds"))) ? <></> : <>{(_.sumBy(data, "unavailability.stopped.seconds") / _.sumBy(data, "unavailability.total.seconds") * 100).toFixed(1)}%</>}</span>
            </div>
          </div>
          <div className="col-span-2 flex flex-col items-end">
            <div className="flex space-x-2 font-semibold">
              <span>{Duration.fromObject({seconds: _.sumBy(data, "availability.off.seconds")}).toFormat("hh:mm:ss")}</span>
              <span>{isNaN((_.sumBy(data, "availability.off.seconds") / _.sumBy(data, "availability.total.seconds"))) ? <></> : <>{(_.sumBy(data, "availability.off.seconds") / _.sumBy(data, "availability.total.seconds") * 100).toFixed(1)}%</>}</span>
            </div>
            <div className="flex space-x-2">
              <span>{Duration.fromObject({seconds: _.sumBy(data, "unavailability.off.seconds")}).toFormat("hh:mm:ss")}</span>
              <span>{isNaN((_.sumBy(data, "unavailability.off.seconds") / _.sumBy(data, "unavailability.total.seconds"))) ? <></> : <>{(_.sumBy(data, "unavailability.off.seconds") / _.sumBy(data, "unavailability.total.seconds") * 100).toFixed(1)}%</>}</span>
            </div>
          </div>
          <div className="col-span-2 flex flex-col items-end">
            <span className="justify-end">{Duration.fromObject({seconds: _.sumBy(data, "availability.total.seconds")}).toFormat("hh:mm:ss")}</span>
            <span className="justify-end">{Duration.fromObject({seconds: _.sumBy(data, "unavailability.total.seconds")}).toFormat("hh:mm:ss")}</span>
          </div>
        </div>
        <Separator />
        {Object.entries(_.groupBy(data, "dateShift.id")).map(([shiftId, shiftData]) => (
          <DayShiftData key={shiftId} equipment={equipment} data={shiftData}/>
        ))}
      </div>
    </div>
  )
}

function DayShiftData({equipment, data}:{equipment: Equipment, data: DataRow[]}) {
  const dateShift = data[0].dateShift
  const [loading, setLoading] = useState(true)
  const [eventsData, setEventsData] = useState<Event[]>(data[0].events)
  const [open, setOpen] = useState(false)


  return (
    <div className="w-full flex flex-col bg-muted border-b border-border last: border-0">
      <div className="grid grid-cols-12 px-4 py-2">
        <div className="col-span-2 flex flex-col justify-start">
          <div className="flex items-center hover:text-primary cursor-pointer" onClick={() => setOpen(!open)}>
            {open ? <TbChevronUp className="w-6" /> : <TbChevronDown className="w-6"/>}
            <div className="font-semibold px-2">{dateShift.start_time.toFormat('HH:mm:ss')} - {dateShift.end_time.toFormat('HH:mm:ss')}</div>
          </div>
          {/* <div className="pl-8">{DateTime.fromSQL(data[0].violations.startWork.eventTime!).toFormat('HH:mm:ss')} - {DateTime.fromSQL(data[0].violations.endWork.eventTime!).toFormat('HH:mm:ss')}</div> */}
        </div>
        <div className="col-span-2 flex flex-col items-end">
          <span className="justify-end">{t('availability')}</span>
          <span className="justify-end">{t('unavailability')}</span>
        </div>

        <div className="col-span-2 flex flex-col items-end">
          <div className="justify-end space-x-2">
            <span  className="font-semibold">{(data[0].availability.worked.percent * 100).toFixed(1)}%</span> 
            <span>{data[0].availability.worked.time}</span>
          </div>
          <div className="justify-end  space-x-2">
            <span className="font-semibold">{(data[0].unavailability.worked.percent * 100).toFixed(1)}%</span> 
            <span>{data[0].unavailability.worked.time}</span>
          </div>
        </div>
        <div className="col-span-2 flex flex-col items-end">
          <div className="justify-end space-x-2">
            <span  className="font-semibold">{(data[0].availability.stopped.percent * 100).toFixed(1)}%</span> 
            <span>{data[0].availability.stopped.time}</span>
          </div>
          <div className="justify-end  space-x-2">
            <span className="font-semibold">{(data[0].unavailability.stopped.percent * 100).toFixed(1)}%</span> 
            <span>{data[0].unavailability.stopped.time}</span>
          </div>
        </div>
        <div className="col-span-2 flex flex-col items-end">
          <div className="justify-end space-x-2">
            <span  className="font-semibold">{(data[0].availability.off.percent * 100).toFixed(1)}%</span> 
            <span>{data[0].availability.off.time}</span>
          </div>
          <div className="justify-end  space-x-2">
            <span className="font-semibold">{(data[0].unavailability.off.percent * 100).toFixed(1)}%</span> 
            <span>{data[0].unavailability.off.time}</span>
          </div>
        </div>
        <div className="col-span-2 flex flex-col items-end">
          <span className="justify-end">{data[0].availability.total.time}</span>
          <span className="justify-end">{data[0].unavailability.total.time}</span>
        </div>
      </div>
      {open ? <div className="px-4 pt-4 pb-2 bg-muted border-border border-b last:border-0">
        <div className="flex flex-col space-y-2" >
          <div className="space-y-2 h-min min-h-20 items-center">
            <TimelineChart events={eventsData!} shift={data[0].dateShift} height={16} width="100%"/>
            <InteractionPersonTimeline events={eventsData} dateShift={data[0].dateShift}/>
            
            <HourHeatMap events={eventsData} shift={data[0].dateShift} height={40} width="100%"/>
            <Grpahs equipment={equipment} dateShift={data[0].dateShift}/>
          </div> 
        </div>
      </div> : <></>}
    </div>
  )
}

function InteractionPersonTimeline({events, dateShift}: {events: Event[]; dateShift: DateShift}) {
  const {persons} = useAppStore()
  let interactions = calculateInteractions(events, dateShift)
  let personIds = _.uniq(interactions.map(i => i.personnel_id))
  const start = interactions && interactions.length > 0 ? DateTime.fromSQL(String(interactions[0].from)) : dateShift.start_day 
  const end = interactions && interactions.length > 0 ? DateTime.fromSQL(String(interactions[interactions.length - 1].to)) : dateShift.end_day 

  const personsConfig: {[id: string]:{name: string, color: string}} = {0: {name: "none", color: hexColor.border}}

  personIds.map(pid => {
    if(pid === 0) {
      personsConfig[Number(pid)] = {name: t('without_person'), color: "rgba(128, 128, 128, 0.5)"}
    } else {
      let person = persons?.find(e => e.id === String(pid))!
      let name = `${person.attributes.last_name!} ${person.attributes.first_name!} ${person.attributes.middle_name!}`
      if(isUndefined(person) || !person) {
        personsConfig[Number(pid)] = {name: t('unknown_person'), color: "rgba(128, 128, 128, 0.5)"}
      } else {
        personsConfig[Number(pid)] = {name: name, color: stringToColour(name)}
      }
      
    }
  })

  const intervals = [
    Interval.fromDateTimes(start, end)
      .splitBy({ hour: 1 })
      .map((d) => d.start?.toISOTime({ includeOffset: false, suppressMilliseconds: true })),
    end.toISOTime({ includeOffset: false, suppressMilliseconds: true }),
  ].flat();

  const shiftDuration = end.toSeconds() - start.toSeconds();

  let graphData: any[] = []
  let style = {
      borderRadius: [50, 50, 50, 50], // Specify the border radius
      borderWidth: 0,
      borderType: 'solid',
      shadowBlur: 0,
      shadowOffsetX: 0,
      shadowOffsetY: 0
  }
  
  

  interactions.map((int, idx) => {
    graphData.push({
      name: personsConfig[Number(int.personnel_id)]?.name,
      type: "bar",
      stack: "timeline",
      color: personsConfig[Number(int.personnel_id)]?.color,
      data: [int.duration],
      itemStyle: style,
    })
  })
    
  const getOptions = () => ({
      tooltip: { 
          show: true
      },
      grid: {
        left: "0%",
        right: "0%",
        bottom: -5,
        top: -5,
        containLabel: false,
      },
      animation: false,
      xAxis: [
        { show: false, max: shiftDuration },
        { show: false, data: intervals },
      ],
      yAxis: { show: false, type: "category" },
      series: graphData,
  });
  
  return interactions.length > 0 && interactions ? <ReactEcharts option={getOptions()} style={{ height: 5, width: "100%" }} opts={{ renderer: "canvas" }} /> : <></>;
}

function ViolationsTimeline({violations, periodSeconds}:{violations: ParameterViolationWithStart[], periodSeconds: number}) {
  const ref = useRef(null);
  const size = useDimensions(ref)
  return (
    <div className="w-full h-4 items-center relative" ref={ref}>
      {violations.map(v => (
        <div style={{position: "absolute", left: `${(size.width / periodSeconds) * v.from_start}px`}}>
          <Popover>
            <PopoverTrigger>
              <TbPointFilled size={12} className="text-red-600" />
            </PopoverTrigger>
            <PopoverContent>
              <div className="text-sm">
                {v.at.toFormat('dd.MM.yyyy HH:mm:ss')}
              </div>
            </PopoverContent>
          </Popover>
          
        </div>
      ))}
    </div>
  )
};

function Grpahs({equipment, dateShift}:{equipment: Equipment, dateShift: DateShift}) {
  const dates = { from: dateShift.start_time.toJSDate(), to: dateShift.end_time.toJSDate() }
  const devices = useAppStore().devices
  console.log(equipment)

  return (
    <div className="flex flex-col gap-2 mt-2">
      {equipment.sensors?.map((sensor) => {
        console.log(sensor)
        return (
          <DeviceSensorData
            key={sensor.id}
            sensorId={sensor.id}
            sensor={sensor.attributes}
            selectedDate={dates}
            showRfid={false}
          />
        );
      })}
    </div>
  );
}